import { apiClient } from './client';

/**
 * The `/v1/keys` management surface — the ONE place a key is born and revoked.
 *
 * It is authenticated with the session JWT, like every other settings call. The
 * keys it manages are the credentials for a different surface entirely
 * (`/v1/public/*`, authenticated with `X-API-Key`): this client never sends one,
 * never stores one, and never sees one again after the single response that
 * creates it.
 */

/** A key as the list returns it. The secret is NOT here and never will be. */
export interface ApiKeySummary {
  id: string;
  /** `akk_` + 7 characters. Enough to recognise a key, useless as a credential. */
  keyPrefix: string;
  /** Already masked server-side: prefix + 20 asterisks. */
  maskedKey: string;
  name: string | null;
  scopes: string[];
  /**
   * When this key last authenticated a request, or `null` if it never has.
   * Coarse by design (the server does not write on every call), so treat it as
   * "recently used", not as an audit timestamp.
   */
  lastUsedAt: string | null;
  createdAt: string;
  /** The user this key acts as — whose credits and conversations it spends. */
  ownerId: string;
  ownerName: string | null;
  ownerEmail: string | null;
}

/**
 * The creation response, and the ONLY moment `key` exists outside the caller's
 * hands. The server stores a SHA-256 of it and nothing else, so a lost key is
 * not recoverable — it is replaced.
 */
export interface CreatedApiKey {
  id: string;
  /** The plaintext secret. Shown once, never returned again by any endpoint. */
  key: string;
  keyPrefix: string;
  name: string | null;
  scopes: string[];
  ownerId: string;
  createdAt: string;
}

export const apiKeysApi = {
  list: () => apiClient.get<ApiKeySummary[]>('/keys'),
  create: (data: { name?: string; scopes: string[] }) =>
    apiClient.post<CreatedApiKey>('/keys', data),
  revoke: (id: string) => apiClient.delete<{ deleted: boolean; id: string }>(`/keys/${id}`),
};
